import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { useAccountant } from "@/lib/auth";
import {
  Users,
  HelpCircle,
  Settings,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API_URL, ApiError, queryClient } from "@/lib/api";
import { QueryError } from "@/components/shared/query-error";
import { TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/accountant/dashboard", label: "Clients", icon: Users },
  { to: "/accountant/help", label: "Help", icon: HelpCircle },
] as const;

export function PortalLayout() {
  const { data: accountant, isLoading, isError, error } = useAccountant();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await fetch(`${API_URL}/api/accountant/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      toast.success("Signed out");
    } catch {
      toast.error("Couldn't reach the server. Signing out locally.");
    } finally {
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  };

  // Auth failures are not a display error — they mean the user isn't logged in
  // and `useAccountant`/`apiFetch` are about to redirect to /login. Render the
  // same loading skeleton so the user doesn't see a red "Can't reach the
  // server" card flash between the 401 landing and the redirect firing.
  const isAuthError =
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403);

  if (isLoading || isAuthError) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <img
          src="/taxr-logo.png"
          alt="Taxr"
          className="h-9 w-auto animate-pulse"
        />
      </div>
    );
  }

  if (isError || !accountant) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-4">
        <QueryError
          className="w-full max-w-md"
          error={error}
          message="Can't reach the server. Refresh your page to continue."
          onRetry={() =>
            queryClient.invalidateQueries({ queryKey: ["accountant-me"] })
          }
        />
      </div>
    );
  }

  const initials = accountant?.business_name
    ? accountant.business_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "TX";

  return (
    <TooltipProvider>
    <div className="min-h-svh bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center px-4 sm:px-6">
          {/* Logo */}
          <Link
            to="/accountant/dashboard"
            className="mr-8 flex items-center gap-2 font-semibold text-lg tracking-tight"
          >
            <img
              src="/taxr-logo.png"
              alt=""
              aria-hidden="true"
              className="h-6 w-auto"
            />
            <span>Taxr</span>
            <span className="text-xs font-medium rounded-full bg-muted text-muted-foreground px-2 py-0.5">
              Portal
            </span>
          </Link>

          {/* Nav */}
          <nav className="flex items-center gap-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )
                }
              >
                <Icon className="size-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Profile menu */}
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="Account menu"
                className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 cursor-pointer"
              >
                <Avatar className="size-8">
                  <AvatarFallback className="text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:inline-block">
                  {accountant?.business_name || accountant?.email}
                </span>
                <ChevronDown className="hidden size-3.5 text-muted-foreground sm:block" aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">
                    {accountant?.business_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {accountant?.email}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/accountant/settings")}
                >
                  <Settings className="mr-2 size-4" aria-hidden />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/accountant/help")}
                >
                  <HelpCircle className="mr-2 size-4" aria-hidden />
                  Help
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="mr-2 size-4" aria-hidden />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-screen-2xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
    </TooltipProvider>
  );
}
